import React, { useEffect, useState } from 'react'
import { Tab, TabItem } from '../../../components/ui/tab'
import Info from './(content)/info';
import Visits from './(content)/visits';
import { Button } from '../../../components/ui/button';
import { Link } from 'react-router-dom';
import { useBoundStore } from '../../../zustand';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../../components/ui/alert-dialog';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangleIcon, Loader, Loader2, PauseCircleIcon, PlayCircleIcon } from 'lucide-react';
import { useTimer } from '../../../context/TimerContext';
import { useInterventionInput } from '../../../context/InterventionInputContext';
import { formatTime } from '../../../lib/time';
import { Input } from '../../../components/ui/input';
import axios from '../../../lib/axios';
import { useAuth } from '../../../context/AuthProvider';
import { format } from 'date-fns';
import { getCustomSettings } from '../../../config/custom-settings';
import { Separator } from '../../../components/ui/separator';
import { Trans, useLingui } from '@lingui/react/macro'
import { checkForAppUpdate } from '../../../hooks/use-version-check';

type Props = {
    task_id?: number;
}

const CancelIntervention = ({ intervention }: { intervention: Intervention }) => {
    const { t } = useLingui()
    const [open, setopen] = useState(false);
    const [reason, setreason] = useState('');

    const { mutate, reset, isPending, isError, isSuccess } = useMutation({
        mutationFn: async () => {

        },
        onError: (e) => {
            console.log(e)
            toast.error('Er is een fout opgetreden bij het annuleren van de interventie')
            setTimeout(() => { reset() }, 1000)
        },
        onSuccess: () => {
            toast.success('Interventie is succesvol geannuleerd')
            setopen(false)
            setTimeout(() => { reset() }, 1000)
        }
    })

    return (
        <AlertDialog open={open} onOpenChange={(o) => { setopen(o) }}>
            <AlertDialogTrigger asChild>
                <Button disabled={isPending || isError || isSuccess} variant={isError ? 'destructive' : 'default'}>
                    {isPending && <Loader2 className='w-4 h-4 animate-spin' />}
                    {isError && <AlertTriangleIcon className='w-4 h-4' />}
                    <Trans>Interventie onderbreken</Trans>
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle><Trans>Interventie onderbreken</Trans></AlertDialogTitle>
                    <AlertDialogDescription><Trans>Verzoek voor een nieuwe afspraak te plannen voor interventie: {intervention.m_sEXTNUMMER}</Trans></AlertDialogDescription>
                </AlertDialogHeader>
                <div>
                    <Label htmlFor='reason'><Trans>Reden</Trans></Label>
                    <Textarea value={reason} onChange={(e) => { setreason(e.target.value) }} placeholder={t`Voer hier de reden in`} id='reason' />
                </div>
                <AlertDialogFooter>
                    <AlertDialogCancel><Trans>Annuleren</Trans></AlertDialogCancel>
                    <AlertDialogAction disabled={isPending || isError || isSuccess} onClick={() => { mutate() }}><Trans>Verstuur</Trans></AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

const ResetIntervention = ({ intervention }: { intervention: Intervention | undefined }) => {
    const [open, setopen] = useState(false);
    const { resetTimer } = useTimer()
    const { resetInterventionInput } = useInterventionInput()

    async function handleReset() {
        resetTimer()
        await resetInterventionInput(intervention?.m_iCALLID?.toString())
        setopen(false)
    }

    return (
        <AlertDialog open={open} onOpenChange={(o) => { setopen(o) }}>
            <AlertDialogTrigger asChild>
                <Button className='w-full'>
                    <Trans>Interventie resetten</Trans>
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle><Trans>Interventie resetten</Trans></AlertDialogTitle>
                    <AlertDialogDescription><Trans>De interventie zal weer open staan maar alle wijzigingen zullen verloren gaan. Wilt u zeker deze interventie resetten?</Trans></AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel><Trans>Annuleren</Trans></AlertDialogCancel>
                    <AlertDialogAction onClick={() => { handleReset() }}><Trans>Ja, reset</Trans></AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

const StartEndRit = ({ intervention }: { intervention: Intervention }) => {
    const { t } = useLingui()
    const { auth } = useAuth()
    const { start_kilometers, setstart_kilometers, start_ride_time, setstart_ride_time, end_kilometers, setend_kilometers, end_ride_time, setend_ride_time, setkilometers, triptime, settriptime } = useInterventionInput()

    const [open, setopen] = useState(false);

    const [s_km, sets_km] = useState('');
    const [s_tm, sets_tm] = useState('');

    const [e_km, sete_km] = useState('');
    const [e_tm, sete_tm] = useState('');

    const enabledKilometers = getCustomSettings(auth.data?.databases[0].dbid).intervention?.settings?.enable_kilometers

    useEffect(() => {
        if (!start_ride_time) {
            sets_tm(format(new Date(), "HH:mm"))
        }
        if (!end_ride_time && start_ride_time) {
            sete_tm(format(new Date(), "HH:mm"))
        }
    }, [open])

    function isComplete() {
        if (!enabledKilometers) return true

        if (!start_kilometers && !start_ride_time) {
            if (!s_km || !s_tm) return false;
            return true;
        } else if (!end_kilometers && !end_ride_time) {
            if (!e_km || !e_tm) return false;
            return true;
        }
        return false;
    }

    function setTripTime() {
        let tmp_tt = triptime ? [...triptime] : []
        let tt = triptime ? triptime[0] : undefined
        if (tt) {
            tt.starttime = s_tm
            tt.endtime = e_tm
        } else {
            tt = { endtime: e_tm, starttime: s_tm, activity: '', interventionid: intervention?.m_iINTERVENTIESID.toString() || '', technicianid: auth.data?.databases[0].technicianid.toString() || '' }
        }
        tmp_tt[0] = tt
        settriptime(tmp_tt)
    }

    if (!!end_ride_time) {
        return <></>
    }

    return (
        <AlertDialog open={open} onOpenChange={setopen}>
            <AlertDialogTrigger asChild>
                <Button>
                    {!start_ride_time ? t`Start rit` : t`Einde rit`}
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle><Trans>Ritgegevens</Trans></AlertDialogTitle>
                    <AlertDialogDescription><Trans>Gelieve onderstaande velden in te vullen</Trans></AlertDialogDescription>
                </AlertDialogHeader>
                {!start_ride_time
                    ?
                    <div>
                        {enabledKilometers &&
                            <div>
                                <Label><Trans>Start kilometerstand</Trans><span className='ml-1 text-red-600'>*</span></Label>
                                <Input value={s_km || ''} onChange={(e) => { sets_km(e.currentTarget.value) }} type='number' />
                            </div>
                        }

                        <div>
                            <Label><Trans>Vertrektijd</Trans><span className='ml-1 text-red-600'>*</span></Label>
                            <Input value={s_tm || ''} onChange={(e) => { sets_tm(e.currentTarget.value) }} type='time' />
                        </div>
                    </div>
                    :
                    <div>
                        {enabledKilometers &&
                            <div>
                                <Label><Trans>Eind kilometerstand</Trans><span className='ml-1 text-red-600'>*</span></Label>
                                <Input value={e_km || ''} onChange={(e) => { sete_km(e.currentTarget.value) }} type='number' />
                            </div>
                        }
                        <div>
                            <Label><Trans>Eind rittijd</Trans><span className='ml-1 text-red-600'>*</span></Label>
                            <Input value={e_tm || ''} onChange={(e) => { sete_tm(e.currentTarget.value) }} type='time' />
                        </div>
                    </div>
                }

                <AlertDialogFooter>
                    <AlertDialogCancel><Trans>Annuleren</Trans></AlertDialogCancel>

                    {!start_ride_time ? <Button disabled={!isComplete()} onClick={() => { setopen(false); setstart_kilometers(parseInt(s_km)); setstart_ride_time(s_tm) }}><Trans>Opslaan</Trans></Button> : <></>}
                    {!(!start_ride_time) ? <Button disabled={!isComplete()} onClick={() => { setTripTime(); setopen(false); setend_kilometers(Number(e_km)); setend_ride_time(e_tm); setkilometers(Number(e_km) - (start_kilometers || 0)) }}><Trans>Opslaan</Trans></Button> : <></>}

                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

const StartIntervention = ({ intervention }: { intervention: Intervention }) => {
    const { t } = useLingui()
    const { auth } = useAuth()
    const { setStartTime, setIsRunning, setId } = useTimer()
    const { setintervention, start_visit, setstart_visit } = useInterventionInput()

    const { mutate } = useMutation({
        mutationFn: async () => {
            const { data } = await axios.post(`checkinatwork?dbid=${auth.data?.databases[0].dbid}`, {
                callid: intervention.m_iCALLID,
                werfid: intervention.m_sWERFID,
                extnummer: intervention.m_sEXTNUMMER,
                iszn: intervention.m_oTECHNIEKER.m_sISZN,
                btwnummer: intervention?.m_oTECHNIEKER.m_sBTWNUMMER,
                appointmentid: intervention.m_iAPPOINTMENTID,
                timestamp: format(new Date(), "yyyyMMddHHmmss"),
            })
            console.log(data);
        },
        onError: (e) => {
            console.log(e)
            toast.error(t`Er is een fout opgetreden bij het aanmelden bij de werkplek`)
        },
        onSuccess: () => {
            toast.success(t`Aanmelden bij werkplek is succesvol gelukt`)
        }
    })

    if (!intervention) return <></>

    if (intervention.m_bCHECKINATWORK.toString() === 'true') {
        return (
            <AlertDialog>
                <AlertDialogTrigger asChild>
                    <Button className='w-full'>
                        <Trans>Start interventie</Trans>
                    </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle><Trans>Check in at work</Trans></AlertDialogTitle>
                        <AlertDialogDescription><Trans>Gelieve onderstaande velden in te vullen</Trans></AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className='flex flex-col gap-2'>
                        <div>
                            <Label><Trans>Werfidentificatie</Trans></Label>
                            <Input defaultValue={intervention.m_sWERFID} disabled />
                        </div>
                        <div>
                            <Label><Trans>Rijksregisternummer</Trans></Label>
                            <Input defaultValue={intervention?.m_oTECHNIEKER?.m_sISZN || ''} disabled />
                        </div>
                        <div>
                            <Label><Trans>BTW nummer</Trans></Label>
                            <Input defaultValue={intervention?.m_oTECHNIEKER?.m_sBTWNUMMER || ''} disabled />
                        </div>
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel><Trans>Annuleren</Trans></AlertDialogCancel>
                        <AlertDialogAction asChild>
                            <Link
                                onClick={() => {
                                    if (!start_visit) { setstart_visit(new Date()) }

                                    mutate()
                                    setId(intervention.m_iINTERVENTIESID.toString());
                                    setStartTime(new Date());

                                    setIsRunning(true);
                                    setintervention(intervention);
                                }}
                                to={`/intervention/start?callid=${intervention.m_iCALLID}`}
                            >
                                <Button
                                    className='w-full'
                                >
                                    <Trans>
                                        Check in
                                    </Trans>
                                </Button>
                            </Link>
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        )
    }

    return (
        <Link
            to={`/intervention/start?callid=${intervention.m_iCALLID}${intervention.m_sTYPE ? `&type=${intervention.m_sTYPE}` : ''}`}
            onClick={() => {
                if (!start_visit) { setstart_visit(new Date()) }

                setId(intervention.m_iINTERVENTIESID.toString());
                setStartTime(new Date());

                setIsRunning(true);
                setintervention(intervention);
            }}
        >
            <Button
                className='w-full'
            >
                <Trans>
                    Start interventie
                </Trans>
            </Button>
        </Link>
    )
}

const Content = ({ task_id }: Props) => {
    const { auth } = useAuth()
    const interventions = useBoundStore((state) => state.interventions)
    const setInterventions = useBoundStore((state) => state.setInterventions)

    const { startTime, elapsedTime, isRunning, setIsRunning, id: timerId, setId } = useTimer()
    const { setintervention } = useInterventionInput()

    const intervention = Array.isArray(interventions) ? interventions?.find((i) => i.m_iCALLID === task_id) : undefined
    const [tab, settab] = useState('info');

    const { isLoading: isLoadingInterventions } = useQuery({
        queryKey: [`interventions`],
        queryFn: async () => {
            await checkForAppUpdate();
            const { data } = await axios.get(`interventions?dbid=${auth.data?.databases[0].dbid}`)
            setInterventions(data);
            return data;
        }
    })

    const runningTimerIntervention = Array.isArray(interventions) ? interventions?.find(i => i.m_iINTERVENTIESID.toString() === timerId) : null
    const filterStatus = getCustomSettings(auth.data?.databases[0].dbid)?.intervention?.start_page?.filter_status || []

    return (
        <div className='flex-1 p-2 overflow-auto md:p-4'>
            <div className='flex justify-center'>
                <Tab value={tab} onValueChange={(v) => { settab(v) }}>
                    <TabItem value='info'><Trans>Info</Trans></TabItem>
                    <TabItem value='visits'><Trans>Bezoeken</Trans></TabItem>
                </Tab>
            </div>

            {((isLoadingInterventions && !intervention) || (isLoadingInterventions && interventions?.length === 0)) &&
                <div className='flex flex-col items-center justify-center mt-2 overflow-auto'>
                    <p className='flex items-center gap-2 text-xl font-medium text-center text-neutral-400'><Loader2 className='w-5 h-5 animate-spin' /><Trans>Even geduld...</Trans></p>
                </div>
            }

            {(!intervention && !isLoadingInterventions || (interventions?.length === 0 && !isLoadingInterventions)) &&
                <div className='flex flex-col mt-2 overflow-auto'>
                    <p className='text-lg font-medium text-center text-neutral-500'><Trans>Geen interventie gevonden</Trans></p>
                </div>
            }

            {intervention &&
                <div className='flex flex-col mt-2 overflow-auto'>
                    {tab === 'info' && <Info intervention={intervention} />}
                    {tab === 'visits' && <Visits intervention={intervention} />}

                    {intervention &&
                        <div className='mt-2'>
                            <Link to={`/startpagina/documents?interventionid=${intervention?.m_iINTERVENTIESID}`}><Button className='w-full'><Trans>Documenten bekijken</Trans></Button></Link>
                        </div>
                    }

                    {((!timerId || (timerId === intervention.m_iINTERVENTIESID.toString())) && !filterStatus.includes(intervention.m_iSTATUS)) &&
                        <div className='flex flex-col gap-2 mt-2 md:mt-4'>

                            <StartEndRit intervention={intervention} />

                            {!startTime
                                ?
                                <StartIntervention intervention={intervention} />
                                :
                                <Link
                                    to={`/intervention/start?callid=${intervention.m_iCALLID}${intervention.m_sTYPE ? `&type=${intervention.m_sTYPE}` : ''}`}
                                    onClick={() => {
                                        if (!isRunning) {
                                            setIsRunning(true);
                                            setId(intervention?.m_iINTERVENTIESID?.toString())
                                        }
                                        setintervention(intervention)
                                    }}
                                >
                                    <Button className='w-full'><Trans>Doorgaan naar rapportage</Trans></Button>
                                </Link>
                            }
                        </div>
                    }
                </div>
            }



            {Number(elapsedTime) > 0 &&
                <>
                    <Separator className='my-6' />
                    <div className='flex flex-col w-full gap-2'>
                        <div className='flex flex-col gap-2 p-2 border rounded-md bg-secondary'>
                            <p className='text-sm font-medium text-neutral-800'><Trans>Actieve interventie (callid: {runningTimerIntervention?.m_iCALLID})</Trans></p>

                            <div className='flex justify-start'>
                                <div className='flex items-center gap-2 p-1 bg-white border rounded-full'>
                                    {isRunning
                                        ? <button onClick={() => { setIsRunning(false) }} className='text-orange-500'><PauseCircleIcon /></button>
                                        : <button onClick={() => { setIsRunning(true) }} className='text-green-500'><PlayCircleIcon /></button>
                                    }
                                    <p>{formatTime(elapsedTime)}</p>
                                </div>
                            </div>

                            <div>

                                <p className='text-sm font-medium'>{runningTimerIntervention?.m_sTYPE}</p>
                                <p className='text-xs line-clamp-2'>{runningTimerIntervention?.m_sOMSCHRIJVING}</p>
                            </div>



                            <ResetIntervention intervention={runningTimerIntervention!} />
                        </div>
                    </div>
                </>
            }


        </div>
    )
}

export default Content